import { IMetadadosDelegacaoMcpResposta } from '../repositorio/conexoesMcp.repositorio.response'
import { ehUsuarioDeSuporte } from '@/typescript/servicos/usuario/usuarioDeSuporte'

export type StatusDelegacaoMcp = 'ativa' | 'expirada' | 'revogada'

export interface IDelegacaoMcp {
  id: string
  criadaEm: Date
  expiraEm: Date
  revogadaEm: Date | null
  status: StatusDelegacaoMcp
}

export function obterStatusDelegacaoMcp (
  delegacao: IMetadadosDelegacaoMcpResposta,
  agora: Date = new Date()
): StatusDelegacaoMcp {
  if (delegacao.revogada_em !== null) return 'revogada'
  if (new Date(delegacao.expira_em).getTime() <= agora.getTime()) return 'expirada'
  return 'ativa'
}

export function normalizarDelegacaoMcp (
  delegacao: IMetadadosDelegacaoMcpResposta,
  agora: Date = new Date()
): IDelegacaoMcp {
  return {
    id: delegacao.id,
    criadaEm: new Date(delegacao.criada_em),
    expiraEm: new Date(delegacao.expira_em),
    revogadaEm: delegacao.revogada_em === null ? null : new Date(delegacao.revogada_em),
    status: obterStatusDelegacaoMcp(delegacao, agora)
  }
}

export function criarConfiguracaoMcp (url: string, token: string): string {
  return JSON.stringify({
    mcpServers: {
      pontotel: {
        type: 'http',
        url,
        headers: { Authorization: `Bearer ${token}` }
      }
    }
  }, null, 2)
}

export interface IUsuarioConexoesMcp {
  ehDeSuporte?: boolean
  email?: string | null
  roles?: Array<{ module?: string, action?: string }>
}

/**
 * Quem abre a tela /conexoes-mcp: suporte da Pontotel (`ehUsuarioDeSuporte`, a
 * régua única do front) com a permissão de chave de API. A tela não está no
 * menu e abre pela URL; esta regra só poupa a pessoa de um 403 — quem recusa
 * de verdade é o backend (`pessoa_pode_delegar_mcp`, que compara o prefixo
 * `lar21@` e por isso é mais estrito: o que ele nega cai em
 * `restrita_ao_atendimento`).
 */
export function podeAcessarConexoesMcp (usuario: IUsuarioConexoesMcp | null | undefined): boolean {
  if (!usuario) return false
  const ehSuporte = ehUsuarioDeSuporte(usuario)
  const temChaveDeApi = (usuario.roles ?? []).some(
    role => role.module === 'appkey' && role.action === 'get'
  )
  return ehSuporte && temChaveDeApi
}

export type MotivoDeRecusaConexoesMcp = 'indisponivel_no_ambiente' | 'restrita_ao_atendimento' | null

/**
 * O que o backend disse ao recusar, quando é algo que a pessoa precisa ler.
 * Sem as chaves de assinatura no ambiente as rotas respondem 503
 * `delegacao_mcp_nao_configurada`; fora do atendimento, 403
 * `delegacao_restrita_ao_atendimento`. O resto é falha genérica.
 */
export function motivoDeRecusaConexoesMcp (erro: unknown): MotivoDeRecusaConexoesMcp {
  const resposta = (erro as { response?: { status?: number, data?: { detail?: { codigo?: string } } } })?.response
  const codigo = resposta?.data?.detail?.codigo
  if (resposta?.status === 503 && codigo === 'delegacao_mcp_nao_configurada') return 'indisponivel_no_ambiente'
  if (resposta?.status === 403 && codigo === 'delegacao_restrita_ao_atendimento') return 'restrita_ao_atendimento'
  return null
}
